import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useDialog } from "../../../components/dialog/DialogContext";
import CardTypes from "../../../components/card-format/CardType";
import { FormatCard } from "../../../components/card-format/FormatCard";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import { Topic } from "../../../components/item-content-two/Topic";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { toast } from "react-toastify";
import allReportStatus from "../../../constants/all-report-status";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { CardFormats } from "../../../models/CardFormats";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { SimpleItemProfile } from "../../../models/SimpleItemProfile";
import { TabSummary } from "../../../models/TabSummary";
import { getCardFormatsById, getCardFormatsWithPagination, storeCardFormat } from "../../../services/cardFormatsService";
import { showLoadItemError } from "../../../services/utilities/ServiceError";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/configuration/card-formats';
const itemPath = '/access-control/configuration/card-format/';

const CardFormat: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.cardTypes), badge: 0, showInitial: true, isActive: true }
    ];
    
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);    

    const tabContentList: ContentWrapper[] = [{
        content: <CardTypes itemId={itemId} tabs={tabs} setTabs={setTabs} />
    }];

    const topic: Topic = {
        accessor: 'trnName',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.cardFormats),
    }

    const initialItem: CardFormats = {
        name: "",
        id: 0,
        tagMask: "FFFFFFFFFFFFFFFFFFFF",
        siteMask: "00000000000000000000",
        cardTypes: 0,
        dropZero: true,
        status:  allReportStatus.Custom.id        
    }    

    const reloadPage = async () => {
        try {
            let cardFormat;
            
            if (itemId) {
                cardFormat = await getCardFormatsById(itemId);
                                                
                setSelectedItem({label: cardFormat.name, value: cardFormat.id.toString()})
                setCurrentItem(cardFormat);
                const updatedTabs = updateTabBadgeTotal(0, cardFormat?.cardTypes, initialTabs);
                setTabs(updatedTabs);
            } else {
                setCurrentItem(initialItem);
                setTabs([]);
            }

            setBreadcrumb([
                { value: '/access-control/configuration', displayName: translate.byKey(TranslationKeys.configuration), disableClick: true },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.cardFormats) },
                { value: itemPath + `${itemId}`, displayName: `${cardFormat?.name ?? ''}` }
            ]);
        } catch (error) {
            const onClick = () => {
                history.push(allItemsPath);
            }
            showLoadItemError(error, dialog,translate, onClick);
        }
    };

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        // Only return the tab content if the tab is active (clicked an existing record)
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
    };

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        
        return newTabs;
    };
   
    const saveCardFormat = (values) => {
        const newCardFormat: CardFormats = {
            ...currentItem,
            name: values.name,
            id: values.id,
            tagMask: values.tagMask,
            siteMask: values.siteMask,
            cardTypes: values.cardTypes,
            dropZero: values.dropZero,
            status:  allReportStatus.Custom.id        
        }
        const isNew = newCardFormat.id ? false : true;
        setBusy(true);
        storeCardFormat(newCardFormat, isNew)
            .then((value) => {
                setCurrentItem(newCardFormat);
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.cardFormatSaved));
                //Not ideal because itemId will call this but keep it here for now some views need double reload to have correct information.
                reloadPage();
            })
            .catch(() => {
                toast.error(translate.byKey(TranslationKeys.applicationError));
            })
            .finally(() => {
                setBusy(false);
            })
    }

    return <TopicContextProvider dataMethod={getCardFormatsWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem}>
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={saveCardFormat}
                tabs={tabs}
                initialValues={currentItem}
                itemPath={itemPath}
                children={
                    <FormatCard setCurrentItem={setCurrentItem} />} />
        </ItemContentTabTwo>
    </TopicContextProvider>
}


export default CardFormat;

